/**
 * Communication step for the Schedule Interview wizard.
 * Dual-pane layout: Candidate (left) | Interviewer (right).
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Flex } from '@chakra-ui/react';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { useInterviewEmailTemplateOptions } from '../../../InterviewActivity/communication/api';
import { useInterviewActivityCommunicationVariables } from '../../../InterviewActivity/communication/api';
import {
  initialInterviewCommunicationFormState,
  interviewCommunicationReducer,
} from '../../../InterviewActivity/communication/state';
import {
  hydrateCommunicationStateFromPayload,
  toInterviewCommunicationPayload,
} from '../../../InterviewActivity/communication/helpers/payloadMap';
import {
  hasValidationErrors,
  validateInterviewCommunicationForm,
  type CommunicationValidationVariables,
  type InterviewCommunicationValidationErrors,
} from '../../../InterviewActivity/communication/validate';
import {
  getFirstScheduleCommunicationScrollTarget,
  scrollScheduleCommunicationSectionIntoView,
} from './scheduleCommunicationScroll';
import { calendarInviteTemplateNameForApi } from '../../../InterviewActivity/communication/helpers/calendarInviteName';
import { compositionFromTemplateOption } from '../../../InterviewActivity/communication/helpers/templateApply';
import { patchEmailComposition } from '../../../InterviewActivity/communication/helpers/emailUtils';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from '../../../InterviewActivity/communication/types';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  resolveDefaultFromValueForCurrentUser,
  useScheduleSenderOptions,
} from '../../hooks/useScheduleSenderOptions';
import type { ScheduleStepProps } from '../../config/types';
import { isAdhocMode, isSelfScheduleMode } from '../../config/modeClassifiers';
import { CandidatePane } from './CandidatePane';
import { InterviewerPane } from './InterviewerPane';
import type { EmailSentBadgeProps } from '../../editMode/EmailSentBadge';

type SentEmailEntry = {
  type: string;
  recipient_id: number;
  recipient_name: string;
};

function buildSentBadge(
  entries: SentEmailEntry[]
): EmailSentBadgeProps | undefined {
  if (!entries.length) return undefined;

  // Count how many emails each unique recipient received.
  const perRecipient = new Map<number, { name: string; count: number }>();
  for (const e of entries) {
    const existing = perRecipient.get(e.recipient_id);
    if (existing) {
      existing.count += 1;
    } else {
      perRecipient.set(e.recipient_id, { name: e.recipient_name, count: 1 });
    }
  }

  // Group recipient names by their individual email count so the tooltip
  // reads "1 email to: Alice, Bob" (each got 1) or "2 emails to: Alice"
  // (Alice got 2) rather than collapsing unrelated totals.
  const byCount = new Map<number, string[]>();
  Array.from(perRecipient.values()).forEach(({ name, count }) => {
    const names = byCount.get(count) ?? [];
    names.push(name);
    byCount.set(count, names);
  });

  const recipients = Array.from(byCount.entries())
    .sort(([a], [b]) => a - b)
    .map(([count, names]) => ({ count, name: names.join(', ') }));

  return { totalSent: entries.length, recipients };
}

export const CommunicationStep = memo(
  forwardRef<
    StepFormApi,
    ScheduleStepProps & { interviewPlan?: Record<string, unknown> }
  >(
    (
      {
        payloadRef,
        mode,
        apiReference: _apiReference,
        prefillVersion = 0,
        interviewPlan,
      },
      ref
    ) => {
      // ---- Reducer state ----
      const [state, dispatch] = useReducer(
        interviewCommunicationReducer,
        initialInterviewCommunicationFormState,
        (base) =>
          hydrateCommunicationStateFromPayload(
            Object.keys(payloadRef.current.communication).length > 0
              ? (payloadRef.current.communication as Partial<
                  Record<string, unknown>
                >)
              : undefined,
            base,
            { stripCalendarTemplates: prefillVersion > 0 }
          )
      );

      const { candidateSentBadge, interviewerSentBadge } = useMemo(() => {
        const all =
          (interviewPlan?.sent_emails as SentEmailEntry[] | undefined) ?? [];
        const candidateEmails = all.filter((e) => e.type === 'candidate');
        const interviewerEmails = all.filter((e) => e.type === 'interviewer');
        return {
          candidateSentBadge: buildSentBadge(candidateEmails),
          interviewerSentBadge: buildSentBadge(interviewerEmails),
        };
      }, [interviewPlan]);

      const validateCommOptions = useMemo(
        () => ({
          validateSelfSchedule: isSelfScheduleMode(mode),
        }),
        [mode]
      );

      useEffect(() => {
        if (validateCommOptions.validateSelfSchedule) {
          return;
        }
        dispatch({ type: 'SET_SELF_SCHEDULE_ENABLED', payload: false });
      }, [dispatch, validateCommOptions.validateSelfSchedule]);

      const [fieldErrors, setFieldErrors] =
        useState<InterviewCommunicationValidationErrors>({});

      // ---- Data fetching ----
      // Match InterviewActivity's per-section fetches so the calendar
      // dropdown only shows calendar-category templates and the email
      // dropdown only shows email-category templates (legacy wizard did
      // the same — a single merged scope left the wrong list in each
      // dropdown and picked the wrong default template).
      const { data: candidateCalendarTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate-calendar');
      const { data: candidateEmailTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate-email');
      const { data: interviewerCalendarTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer-calendar');
      const { data: interviewerEmailTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer-email');
      const { data: selfScheduleTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('self-schedule');
      const {
        fromOptions,
        ccBccOptions,
        isFromOptionsFetched,
        isFromOptionsLoading,
      } = useScheduleSenderOptions();

      // The shared initial state seeds `from: 'organizer'` — that
      // sentinel is correct on InterviewActivity (stage-action plan),
      // but for a specific scheduled interview the From should default
      // to the user creating it. Swap each email's `from` once on mount
      // iff (a) it still holds the organizer sentinel AND (b) the
      // current user is actually delegatable. The second check closes
      // the bug where an admin-only user had their id written into
      // `from`, the Select showed empty (id not in fromOptions), and
      // the validator reported the form valid anyway.
      const currUser = useSelector(selectCurrUser) as
        | { id?: number }
        | null
        | undefined;
      const didSeedFromRef = useRef(false);
      useEffect(() => {
        if (didSeedFromRef.current) return;
        if (!isFromOptionsFetched) return;
        const fromValue = resolveDefaultFromValueForCurrentUser(
          fromOptions,
          currUser?.id
        );
        if (!fromValue) return;
        didSeedFromRef.current = true;
        if (
          state.candidateEmail.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_CANDIDATE_EMAIL',
            payload: patchEmailComposition(state.candidateEmail, {
              from: fromValue,
            }),
          });
        }
        if (
          state.interviewerEmail.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_INTERVIEWER_EMAIL',
            payload: patchEmailComposition(state.interviewerEmail, {
              from: fromValue,
            }),
          });
        }
        if (
          state.selfSchedule.from === INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
        ) {
          dispatch({
            type: 'SET_SELF_SCHEDULE',
            payload: patchEmailComposition(state.selfSchedule, {
              from: fromValue,
            }),
          });
        }
        // Intentionally only read state.* once — this is a one-shot seed
        // keyed on the ref, not a live sync.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currUser?.id, isFromOptionsFetched, fromOptions]);

      const {
        candidateCalendar: candidateCalendarVariables,
        interviewerCalendar: interviewerCalendarVariables,
        candidateEmail: candidateEmailVariables,
        interviewerEmail: interviewerEmailVariables,
      } = useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();
      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      // ---- Validation variables ----
      const validationVariables: CommunicationValidationVariables = useMemo(
        () => ({
          candidateCalendar: candidateCalendarVariables,
          interviewerCalendar: interviewerCalendarVariables,
          candidateEmail: candidateEmailVariables,
          interviewerEmail: interviewerEmailVariables,
        }),
        [
          candidateCalendarVariables,
          candidateEmailVariables,
          interviewerCalendarVariables,
          interviewerEmailVariables,
        ]
      );

      // ---- Live validation after first submit failure ----
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!hasValidationErrors(prev)) return prev;
          return validateInterviewCommunicationForm(
            state,
            validationVariables,
            validateCommOptions
          );
        });
      }, [state, validationVariables, validateCommOptions]);

      // ---- One-shot template defaults (adhoc create only) ----
      // Adhoc flows have no interview plan to pull templates from, so
      // we fall back to the first available template per section to
      // avoid a blank editor. Manual create is intentionally excluded
      // — `useScheduleInterview` seeds `payloadRef.communication`
      // from the plan's comm fields before this step mounts, and
      // those templates are the source of truth. Edit mode is gated
      // by `prefillVersion > 0` as before.
      const isAdhoc = isAdhocMode(mode);
      const didDefaultCandidateCal = useRef(false);
      const didDefaultCandidateEmail = useRef(false);
      const didDefaultInterviewerCal = useRef(false);
      const didDefaultInterviewerEmail = useRef(false);
      const didDefaultSelfSchedule = useRef(false);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !candidateCalendarTemplateOptions.length) {
          return;
        }
        if (state.candidateCalendar.template) {
          didDefaultCandidateCal.current = true;
          return;
        }
        if (didDefaultCandidateCal.current) return;
        didDefaultCandidateCal.current = true;
        dispatch({
          type: 'SET_CANDIDATE_CALENDAR',
          payload: compositionFromTemplateOption(
            state.candidateCalendar,
            candidateCalendarTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        candidateCalendarTemplateOptions,
        state.candidateCalendar,
      ]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !candidateEmailTemplateOptions.length) {
          return;
        }
        if (state.candidateEmail.template) {
          didDefaultCandidateEmail.current = true;
          return;
        }
        if (didDefaultCandidateEmail.current) return;
        didDefaultCandidateEmail.current = true;
        dispatch({
          type: 'SET_CANDIDATE_EMAIL',
          payload: compositionFromTemplateOption(
            state.candidateEmail,
            candidateEmailTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        candidateEmailTemplateOptions,
        state.candidateEmail,
      ]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !interviewerCalendarTemplateOptions.length) {
          return;
        }
        if (state.interviewerCalendar.template) {
          didDefaultInterviewerCal.current = true;
          return;
        }
        if (didDefaultInterviewerCal.current) return;
        didDefaultInterviewerCal.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_CALENDAR',
          payload: compositionFromTemplateOption(
            state.interviewerCalendar,
            interviewerCalendarTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        interviewerCalendarTemplateOptions,
        state.interviewerCalendar,
      ]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !interviewerEmailTemplateOptions.length) {
          return;
        }
        if (state.interviewerEmail.template) {
          didDefaultInterviewerEmail.current = true;
          return;
        }
        if (didDefaultInterviewerEmail.current) return;
        didDefaultInterviewerEmail.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_EMAIL',
          payload: compositionFromTemplateOption(
            state.interviewerEmail,
            interviewerEmailTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        interviewerEmailTemplateOptions,
        state.interviewerEmail,
      ]);

      useEffect(() => {
        if (!isAdhoc) return;
        if (prefillVersion > 0 || !selfScheduleTemplateOptions.length) return;
        if (!validateCommOptions.validateSelfSchedule) return;
        if (state.selfSchedule.template) {
          didDefaultSelfSchedule.current = true;
          return;
        }
        if (didDefaultSelfSchedule.current) return;
        didDefaultSelfSchedule.current = true;
        dispatch({
          type: 'SET_SELF_SCHEDULE',
          payload: compositionFromTemplateOption(
            state.selfSchedule,
            selfScheduleTemplateOptions[0]
          ),
        });
      }, [
        isAdhoc,
        prefillVersion,
        selfScheduleTemplateOptions,
        state.selfSchedule,
        validateCommOptions.validateSelfSchedule,
      ]);

      // ---- Imperative handle ----
      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateInterviewCommunicationForm(
              state,
              validationVariables,
              validateCommOptions
            );
            setFieldErrors(errors);
            const ok = !hasValidationErrors(errors);
            if (!ok) {
              const target = getFirstScheduleCommunicationScrollTarget(errors);
              if (target) {
                scrollScheduleCommunicationSectionIntoView(target);
              }
            }
            return ok;
          },
          syncToPayload: () => {
            const comm = toInterviewCommunicationPayload(state);
            comm._ui_calendar_candidate_name = calendarInviteTemplateNameForApi(
              state.candidateCalendar,
              candidateCalendarTemplateOptions
            );
            comm._ui_calendar_interviewer_name =
              calendarInviteTemplateNameForApi(
                state.interviewerCalendar,
                interviewerCalendarTemplateOptions
              );
            payloadRef.current.communication = comm as unknown as Record<
              string,
              unknown
            >;
          },
        }),
        [
          candidateCalendarTemplateOptions,
          interviewerCalendarTemplateOptions,
          payloadRef,
          state,
          validationVariables,
          validateCommOptions,
        ]
      );

      // ---- Render ----
      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          gap="6"
          overflow="auto"
          pb="8"
        >
          <CandidatePane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            calendarTemplateOptions={candidateCalendarTemplateOptions}
            emailTemplateOptions={candidateEmailTemplateOptions}
            fromOptions={fromOptions}
            isFromOptionsLoading={isFromOptionsLoading}
            ccBccOptions={ccBccOptions}
            calendarVariables={candidateCalendarVariables}
            emailVariables={candidateEmailVariables}
            uploadInlineImage={uploadInlineImage}
            sentEmailBadge={candidateSentBadge}
          />
          <InterviewerPane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            calendarTemplateOptions={interviewerCalendarTemplateOptions}
            emailTemplateOptions={interviewerEmailTemplateOptions}
            fromOptions={fromOptions}
            isFromOptionsLoading={isFromOptionsLoading}
            ccBccOptions={ccBccOptions}
            calendarVariables={interviewerCalendarVariables}
            emailVariables={interviewerEmailVariables}
            uploadInlineImage={uploadInlineImage}
            sentEmailBadge={interviewerSentBadge}
          />
        </Flex>
      );
    }
  )
);

CommunicationStep.displayName = 'CommunicationStep';
